"use client";

import type React from "react";
import { useRef } from "react";
import { motion, useInView, useReducedMotion } from "@/lib/motion";
import { Check, Copy, Lock, Plus, RefreshCw, ShieldCheck } from "@/components/icons";

/* Ambient looping visuals for product feature grids — each one is a small,
   recognizable product moment (a device joining, a request log, a mode
   switch, an endpoint spinning up) rather than an abstract glyph. Built from
   bg-popover chips over the page surface, token-based so they read in both
   themes. Every loop shares the same 6s cycle and falls back to a static
   "settled" state under reduced motion. All are decorative. */

const LOOP = { duration: 6, repeat: Infinity, ease: "easeInOut" as const };

/** Eases back to the resting state when a loop switches off, instead of
 *  snapping. */
const SETTLE = { duration: 0.25, ease: "easeOut" as const };

/** Ambient loops only run while their card is on screen — a six-up grid of
 *  always-on timelines is a lot of frames spent on nothing. False under reduced
 *  motion too, so each visual has one flag to branch on and one resting state to
 *  fall back to.
 *
 *  Every animated element here pairs an explicit `initial` with its first
 *  keyframe. `initial={false}` resolves an element to the LAST value in its
 *  keyframe array and skips the loop, which for these visuals (most of which
 *  fade back out at the end of a cycle) meant rendering a blank card. */
function useAmbientLoop() {
  const ref = useRef<HTMLDivElement>(null);
  const reduced = useReducedMotion();
  const inView = useInView(ref, { amount: 0.5 });
  return { ref, active: !reduced && inView };
}

const GREEN_TINT = "color-mix(in srgb, var(--brand-green) 14%, transparent)";
const CYAN_TINT = "color-mix(in srgb, var(--brand-yellow) 14%, transparent)";

interface LabelPillProps {
  children: React.ReactNode;
}

/** Floating uppercase label chip anchoring a visual. */
function LabelPill({ children }: LabelPillProps) {
  return (
    <span
      className="border-border bg-popover rounded-full border px-2.5 py-1 text-[8px] font-semibold tracking-wider whitespace-nowrap uppercase shadow-sm"
      style={{ color: "var(--brand-green)" }}
    >
      {children}
    </span>
  );
}

/* ── Premium Mobile IPs — a live device identity chip ───────────────────── */

const SIGNAL_BARS = [
  { id: "b1", height: "h-1.5", at: 0.1 },
  { id: "b2", height: "h-2.5", at: 0.16 },
  { id: "b3", height: "h-3.5", at: 0.22 },
  { id: "b4", height: "h-4.5", at: 0.28 },
];

export function CarrierSignalVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-2"
      aria-hidden="true"
    >
      <div className="border-border bg-popover flex items-center gap-2.5 rounded-xl border px-3.5 py-2 shadow-sm">
        <span className="flex items-end gap-0.5">
          {SIGNAL_BARS.map((bar) => (
            <span
              key={bar.id}
              className={`bg-foreground/15 relative w-1 rounded-full ${bar.height}`}
            >
              <motion.span
                initial={{ opacity: 0.4 }}
                animate={active ? { opacity: [0.4, 0.4, 1, 1, 0.4] } : { opacity: 1 }}
                transition={
                  active ? { ...LOOP, times: [0, bar.at, bar.at + 0.05, 0.88, 0.94] } : SETTLE
                }
                className="absolute inset-0 rounded-full"
                style={{ backgroundColor: "var(--brand-green)" }}
              />
            </span>
          ))}
        </span>
        <span className="flex flex-col">
          <span className="text-foreground text-[10px] leading-tight font-semibold">
            5G · Mobile IP
          </span>
          <span className="text-muted-foreground font-mono text-[9px] leading-tight">
            100.64.12.7 · AS21928
          </span>
        </span>
      </div>
      <motion.span
        initial={{ opacity: 0.4, y: 4 }}
        animate={
          active ? { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] } : { opacity: 1, y: 0 }
        }
        transition={active ? { ...LOOP, times: [0, 0.4, 0.46, 0.88, 0.94] } : SETTLE}
        className="flex items-center gap-1 text-[8px] font-semibold tracking-wider uppercase"
        style={{ color: "var(--brand-green)" }}
      >
        <Check size={9} aria-hidden="true" /> Ethically sourced · Opt-in
      </motion.span>
    </div>
  );
}

/* ── Precision Geo-targeting — a pin locking onto a city ────────────────── */

const GEO_DOTS = Array.from({ length: 15 }, (_, i) => `g${i}`);

export function GeoTargetVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-2.5"
      aria-hidden="true"
    >
      <div className="relative">
        <div className="grid grid-cols-5 gap-2.5">
          {GEO_DOTS.map((dot) => (
            <span key={dot} className="bg-foreground/15 h-1 w-1 rounded-full" />
          ))}
        </div>
        {active && (
          <motion.span
            initial={{ scale: 0.4, opacity: 0.7 }}
            animate={{ scale: [0.4, 1.7], opacity: [0.7, 0] }}
            transition={{ duration: 1.8, repeat: Infinity, repeatDelay: 0.5, ease: "easeOut" }}
            className="absolute top-1/2 left-1/2 h-8 w-8 rounded-full"
            style={{ x: "-50%", y: "-50%", border: "1.5px solid var(--brand-green)" }}
          />
        )}
        {/* Positioning lives on the wrapper: the pin animates `y`, and motion
            owns the whole transform on whatever element it drives. */}
        <span className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[85%]">
          <motion.span
            initial={{ opacity: 0.4, y: -8 }}
            animate={
              active
                ? { opacity: [0.4, 0.4, 1, 1, 0.4], y: [-8, -8, 0, 0, -8] }
                : { opacity: 1, y: 0 }
            }
            transition={active ? { ...LOOP, times: [0, 0.06, 0.14, 0.9, 0.96] } : SETTLE}
            className="block text-[14px] leading-none"
          >
            📍
          </motion.span>
        </span>
      </div>
      <motion.span
        initial={{ opacity: 0.4, scale: 0.9 }}
        animate={
          active
            ? { opacity: [0.4, 0.4, 1, 1, 0.4], scale: [0.9, 0.9, 1, 1, 0.9] }
            : { opacity: 1, scale: 1 }
        }
        transition={active ? { ...LOOP, times: [0, 0.26, 0.32, 0.88, 0.94] } : SETTLE}
        className="border-border bg-popover flex items-center gap-1.5 rounded-full border px-2.5 py-1 shadow-sm"
      >
        <span className="text-foreground text-[9px] font-medium">New York, US · City-level</span>
        <Check size={9} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
      </motion.span>
    </div>
  );
}

/* ── Success Rate — a request log where everything comes back 200 ───────── */

const REQUESTS = [
  { id: "r1", path: "GET /search?q=…", at: 0.08 },
  { id: "r2", path: "GET /products/4821", at: 0.18 },
  { id: "r3", path: "GET /reviews?page=2", at: 0.28 },
];

interface SuccessRateVisualProps {
  percent?: string;
}

export function SuccessRateVisual({ percent = "99.5%" }: SuccessRateVisualProps) {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      {REQUESTS.map((req) => (
        <motion.span
          key={req.id}
          initial={{ opacity: 0.4, y: 4 }}
          animate={
            active ? { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] } : { opacity: 1, y: 0 }
          }
          transition={active ? { ...LOOP, times: [0, req.at, req.at + 0.06, 0.88, 0.94] } : SETTLE}
          className="border-border bg-popover flex w-44 items-center justify-between gap-2 rounded-lg border px-2.5 py-1 shadow-sm"
        >
          <span className="text-muted-foreground truncate font-mono text-[9px]">{req.path}</span>
          <motion.span
            initial={{ opacity: 0.4 }}
            animate={active ? { opacity: [0.4, 0.4, 1, 1, 0.4] } : { opacity: 1 }}
            transition={
              active ? { ...LOOP, times: [0, req.at + 0.08, req.at + 0.13, 0.88, 0.94] } : SETTLE
            }
            className="shrink-0 rounded-full px-1.5 py-px font-mono text-[8px] font-semibold"
            style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
          >
            200
          </motion.span>
        </motion.span>
      ))}
      <motion.span
        initial={{ opacity: 0.4 }}
        animate={active ? { opacity: [0.4, 0.4, 1, 1, 0.4] } : { opacity: 1 }}
        transition={active ? { ...LOOP, times: [0, 0.48, 0.54, 0.88, 0.94] } : SETTLE}
        className="flex items-center gap-1 text-[8px] font-semibold tracking-wider uppercase"
        style={{ color: "var(--brand-green)" }}
      >
        <Check size={9} aria-hidden="true" /> {percent} target success rate
      </motion.span>
    </div>
  );
}

/* ── Rotating & Sticky Sessions — switching modes, IP follows ───────────── */

const IP_A = "172.58.14.102";
const IP_B = "107.77.201.36";

interface ModeChipProps {
  label: string;
  /** Opacity keyframes for the active-state overlay across the loop. */
  activeTimes: number[];
  activeKeyframes: number[];
  /** Whether the parent's ambient loop is running. */
  loopActive: boolean;
  /** Which chip stays lit when the loop is off. */
  staticActive: boolean;
}

function ModeChip({
  label,
  activeTimes,
  activeKeyframes,
  loopActive,
  staticActive,
}: ModeChipProps) {
  return (
    <span className="border-border bg-popover relative overflow-hidden rounded-full border px-2.5 py-1 shadow-sm">
      <motion.span
        initial={{ opacity: activeKeyframes[0] }}
        animate={loopActive ? { opacity: activeKeyframes } : { opacity: staticActive ? 1 : 0 }}
        transition={loopActive ? { ...LOOP, times: activeTimes } : SETTLE}
        className="absolute inset-0"
        style={{ backgroundColor: GREEN_TINT }}
      />
      <span className="text-foreground relative text-[8px] font-semibold tracking-wider uppercase">
        {label}
      </span>
    </span>
  );
}

export function RotationVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-2"
      aria-hidden="true"
    >
      <div className="flex items-center gap-1.5">
        <ModeChip
          label="Rotating"
          activeKeyframes={[1, 1, 0, 0, 1]}
          activeTimes={[0, 0.55, 0.57, 0.93, 0.95]}
          loopActive={active}
          staticActive
        />
        <ModeChip
          label="Sticky"
          activeKeyframes={[0, 0, 1, 1, 0]}
          activeTimes={[0, 0.55, 0.57, 0.93, 0.95]}
          loopActive={active}
          staticActive={false}
        />
      </div>
      <div className="border-border bg-popover flex items-center gap-2 rounded-lg border px-3 py-1.5 shadow-sm">
        <span className="relative font-mono text-[10px]">
          <span className="invisible">{IP_A}</span>
          <motion.span
            initial={{ opacity: 1 }}
            animate={active ? { opacity: [1, 1, 0, 0, 1] } : { opacity: 1 }}
            transition={active ? { ...LOOP, times: [0, 0.24, 0.26, 0.93, 0.95] } : SETTLE}
            className="text-foreground absolute inset-0"
          >
            {IP_A}
          </motion.span>
          <motion.span
            initial={{ opacity: 0 }}
            animate={active ? { opacity: [0, 0, 1, 1, 0] } : { opacity: 0 }}
            transition={active ? { ...LOOP, times: [0, 0.24, 0.26, 0.93, 0.95] } : SETTLE}
            className="text-foreground absolute inset-0"
          >
            {IP_B}
          </motion.span>
        </span>
        <span className="relative flex h-3 w-3 shrink-0 items-center justify-center">
          <motion.span
            initial={{ rotate: 0, opacity: 1 }}
            animate={
              active
                ? {
                    rotate: [0, 0, 360, 360, 360, 360],
                    opacity: [1, 1, 1, 0, 0, 1],
                  }
                : { rotate: 0, opacity: 1 }
            }
            /* Exactly complementary to the lock below, and covering the FULL
               cycle. Stopping the keyframes at 0.54 left the icon interpolating
               back up across the lock's visible window, so both were drawn at
               once for about a second. */
            transition={active ? { ...LOOP, times: [0, 0.2, 0.55, 0.57, 0.93, 0.95] } : SETTLE}
            className="absolute flex"
          >
            <RefreshCw size={11} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
          </motion.span>
          {active && (
            <motion.span
              initial={{ opacity: 0 }}
              animate={{ opacity: [0, 0, 1, 1, 0] }}
              transition={{ ...LOOP, times: [0, 0.55, 0.57, 0.93, 0.95] }}
              className="absolute flex"
            >
              <Lock size={10} className="text-muted-foreground" aria-hidden="true" />
            </motion.span>
          )}
        </span>
      </div>
      <motion.span
        initial={{ opacity: 1 }}
        animate={active ? { opacity: [1, 1, 0, 0, 1] } : { opacity: 1 }}
        transition={active ? { ...LOOP, times: [0, 0.55, 0.57, 0.93, 0.95] } : SETTLE}
        className="text-muted-foreground text-[8px]"
      >
        New exit IP on every request
      </motion.span>
    </div>
  );
}

/* ── Unlimited Access Points — clicking "+ New" spins one up ────────────── */

const ACCESS_POINTS = [
  { id: "ap1", host: "pg.proxi.es:20101", at: 0.06 },
  { id: "ap2", host: "pg.proxi.es:20102", at: 0.14 },
];

export function AccessPointsVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      {ACCESS_POINTS.map((point) => (
        <motion.span
          key={point.id}
          initial={{ opacity: 0.4, y: 4 }}
          animate={
            active ? { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] } : { opacity: 1, y: 0 }
          }
          transition={
            active ? { ...LOOP, times: [0, point.at, point.at + 0.06, 0.88, 0.94] } : SETTLE
          }
          className="border-border bg-popover flex items-center gap-1.5 rounded-full border px-2.5 py-1 shadow-sm"
        >
          <span
            className="h-1 w-1 rounded-full"
            style={{ backgroundColor: "var(--brand-green)" }}
          />
          <span className="text-foreground font-mono text-[9px]">{point.host}</span>
          <span className="text-muted-foreground text-[8px]">HTTP</span>
        </motion.span>
      ))}

      {/* The "+ New access point" button gets pressed mid-loop… */}
      <motion.span
        initial={{ opacity: 0.4, scale: 1 }}
        animate={
          active
            ? { opacity: [0.4, 0.4, 1, 1, 1, 1, 0.4], scale: [1, 1, 1, 0.92, 1, 1, 1] }
            : { opacity: 1, scale: 1 }
        }
        transition={active ? { ...LOOP, times: [0, 0.22, 0.28, 0.4, 0.44, 0.88, 0.94] } : SETTLE}
        className="flex items-center gap-1 rounded-full px-2.5 py-1 text-[8px] font-semibold shadow-sm"
        style={{ backgroundColor: "var(--brand-green)", color: "var(--background)" }}
      >
        <Plus size={9} aria-hidden="true" /> New access point
      </motion.span>

      {/* …and a fresh endpoint joins the list with a brief highlight. */}
      <motion.span
        initial={{ opacity: 0.4, y: 4 }}
        animate={
          active ? { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] } : { opacity: 1, y: 0 }
        }
        transition={active ? { ...LOOP, times: [0, 0.48, 0.54, 0.88, 0.94] } : SETTLE}
        className="border-border bg-popover relative flex items-center gap-1.5 rounded-full border px-2.5 py-1 shadow-sm"
      >
        <motion.span
          initial={{ opacity: 0.4 }}
          animate={active ? { opacity: [0.4, 0.4, 0.8, 0.4, 0.4] } : { opacity: 0 }}
          transition={active ? { ...LOOP, times: [0, 0.48, 0.56, 0.72, 1] } : SETTLE}
          className="absolute inset-0 rounded-full"
          style={{ backgroundColor: GREEN_TINT }}
        />
        <span
          className="relative h-1 w-1 rounded-full"
          style={{ backgroundColor: "var(--brand-green)" }}
        />
        <span className="text-foreground relative font-mono text-[9px]">pg.proxi.es:20103</span>
        <span className="text-muted-foreground relative text-[8px]">HTTP</span>
      </motion.span>
    </div>
  );
}

/* ── Ready-to-copy Credentials — one click on Copy, done ────────────────── */

export function CredentialsVisual() {
  const { ref, active } = useAmbientLoop();
  return (
    <div
      ref={ref}
      className="flex h-28 w-full flex-col items-center justify-center gap-2"
      aria-hidden="true"
    >
      <div className="relative">
        <div className="border-border bg-popover flex items-center gap-2 rounded-lg border py-1.5 pr-1.5 pl-3 shadow-sm">
          <span className="flex flex-col">
            <span className="text-muted-foreground text-[7px] tracking-wider uppercase">
              Proxy credentials
            </span>
            <span className="text-foreground font-mono text-[9px]">
              user:••••@pg.proxi.es:20000
            </span>
          </span>
          <motion.span
            initial={{ scale: 1 }}
            animate={active ? { scale: [1, 1, 0.88, 1, 1] } : { scale: 1 }}
            transition={active ? { ...LOOP, times: [0, 0.34, 0.38, 0.42, 1] } : SETTLE}
            className="bg-foreground/5 flex h-6 w-6 shrink-0 items-center justify-center rounded-md"
          >
            <Copy size={11} className="text-muted-foreground" aria-hidden="true" />
          </motion.span>
        </div>
        <motion.span
          initial={{ opacity: 0, scale: 0.85 }}
          animate={
            active
              ? { opacity: [0, 0, 1, 1, 0], scale: [0.85, 0.85, 1, 1, 0.85] }
              : { opacity: 1, scale: 1 }
          }
          transition={active ? { ...LOOP, times: [0, 0.42, 0.48, 0.85, 0.92] } : SETTLE}
          className="absolute -top-3 right-0 flex items-center gap-1 rounded-full px-2 py-0.5 shadow-sm"
          style={{ backgroundColor: "var(--brand-green)", color: "var(--background)" }}
        >
          <Check size={8} aria-hidden="true" />
          <span className="text-[8px] font-semibold tracking-wider uppercase">Copied</span>
        </motion.span>
      </div>
      <motion.span
        initial={{ opacity: 0.4 }}
        animate={active ? { opacity: [0.4, 0.4, 1, 1, 0.4] } : { opacity: 1 }}
        transition={active ? { ...LOOP, times: [0, 0.52, 0.58, 0.88, 0.94] } : SETTLE}
        className="text-muted-foreground font-mono text-[8px]"
      >
        curl -x http://user:pass@pg.proxi.es:20000 …
      </motion.span>
    </div>
  );
}

/* ── Residential IP pool — global exits joining the pool ────────────────── */

const POOL_IPS = [
  { id: "ip1", flag: "🇺🇸", ip: "98.112.4.21", at: 0.08 },
  { id: "ip2", flag: "🇩🇪", ip: "84.171.9.30", at: 0.2 },
  { id: "ip3", flag: "🇯🇵", ip: "126.34.88.7", at: 0.32 },
];

export function IpPoolVisual() {
  const reduced = useReducedMotion();
  return (
    <div
      className="flex h-28 w-full flex-col items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      {POOL_IPS.map((entry) => (
        <motion.span
          key={entry.id}
          initial={false}
          animate={
            reduced ? { opacity: 1, y: 0 } : { opacity: [0.4, 0.4, 1, 1, 0.4], y: [5, 5, 0, 0, 5] }
          }
          transition={
            reduced
              ? { duration: 0 }
              : { ...LOOP, times: [0, entry.at, entry.at + 0.06, 0.88, 0.94] }
          }
          className="border-border bg-popover flex items-center gap-1.5 rounded-full border px-2.5 py-1 shadow-sm"
        >
          <span className="text-[10px] leading-none">{entry.flag}</span>
          <span className="text-foreground font-mono text-[9px]">{entry.ip}</span>
        </motion.span>
      ))}
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.46, 0.52, 0.88, 0.94] }}
        className="border-border text-muted-foreground flex items-center gap-1 rounded-full border border-dashed px-2.5 py-1 text-[9px]"
      >
        <Plus size={9} aria-hidden="true" /> 195+ countries
      </motion.span>
    </div>
  );
}

/* ── Parallel connections — concurrent requests racing through ──────────── */

const THREADS = [
  { id: "t1", delay: 0 },
  { id: "t2", delay: 0.35 },
  { id: "t3", delay: 0.7 },
  { id: "t4", delay: 1.05 },
];

export function ConcurrencyVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-3" aria-hidden="true">
      <div className="flex w-40 flex-col gap-2">
        {THREADS.map((thread) => (
          <span
            key={thread.id}
            className="bg-foreground/10 relative h-1 overflow-hidden rounded-full"
          >
            {reduced ? (
              <span
                className="absolute inset-y-0 right-0 w-8 rounded-full"
                style={{ backgroundColor: "var(--brand-green)" }}
              />
            ) : (
              <motion.span
                initial={false}
                animate={{ x: ["-2.5rem", "10.5rem"] }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  repeatDelay: 0.5,
                  ease: "easeInOut",
                  delay: thread.delay,
                }}
                className="absolute inset-y-0 left-0 w-8 rounded-full"
                style={{ backgroundColor: "var(--brand-green)" }}
              />
            )}
          </span>
        ))}
      </div>
      <LabelPill>Parallel connections</LabelPill>
    </div>
  );
}

/* ── Bandwidth rollover — unused GBs carrying into next cycle ───────────── */

export function RolloverVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-3" aria-hidden="true">
      <div className="relative w-40">
        <div className="bg-foreground/10 h-2 w-full overflow-hidden rounded-full">
          <motion.span
            initial={false}
            animate={reduced ? { scaleX: 1 } : { scaleX: [0, 0, 1, 1, 0] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.08, 0.34, 0.9, 0.96] }}
            className="block h-full w-[62%] origin-left rounded-full"
            style={{ backgroundColor: "var(--brand-green)" }}
          />
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.36, 0.42, 0.9, 0.96] }}
            className="absolute inset-y-0 right-0 w-[38%] rounded-r-full"
            style={{ backgroundColor: CYAN_TINT }}
          />
        </div>
        <motion.span
          initial={false}
          animate={
            reduced
              ? { opacity: 1, scale: 1 }
              : { opacity: [0, 0, 1, 1, 0], scale: [0.85, 0.85, 1, 1, 0.85] }
          }
          transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.46, 0.52, 0.88, 0.94] }}
          className="border-border bg-popover absolute -top-6 right-0 flex items-center gap-1 rounded-full border px-2 py-0.5 shadow-sm"
        >
          <RefreshCw size={8} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
          <span className="text-foreground text-[8px] font-semibold whitespace-nowrap">
            3.8 GB rolls over
          </span>
        </motion.span>
      </div>
      <LabelPill>No wasted GBs</LabelPill>
    </div>
  );
}

/* ── SOC 2 & ISO 27001 — certification badges landing ───────────────────── */

const CERT_BADGES = [
  { id: "c1", label: "SOC 2 · Type II", at: 0.1 },
  { id: "c2", label: "ISO 27001", at: 0.22 },
];

export function CertificationBadgesVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-2" aria-hidden="true">
      <div className="flex items-center gap-2">
        {CERT_BADGES.map((badge) => (
          <motion.span
            key={badge.id}
            initial={false}
            animate={
              reduced
                ? { opacity: 1, scale: 1 }
                : { opacity: [0.4, 0.4, 1, 1, 0.4], scale: [0.88, 0.88, 1, 1, 0.88] }
            }
            transition={
              reduced
                ? { duration: 0 }
                : { ...LOOP, times: [0, badge.at, badge.at + 0.06, 0.88, 0.94] }
            }
            className="border-border bg-popover flex items-center gap-1.5 rounded-xl border px-3 py-2 shadow-sm"
          >
            <ShieldCheck size={13} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
            <span className="text-foreground text-[10px] font-semibold">{badge.label}</span>
          </motion.span>
        ))}
      </div>
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.38, 0.44, 0.88, 0.94] }}
        className="flex items-center gap-1 text-[8px] font-semibold tracking-wider uppercase"
        style={{ color: "var(--brand-green)" }}
      >
        <Check size={9} aria-hidden="true" /> Independently audited
      </motion.span>
    </div>
  );
}

/* ── Real-time Dashboard — a live bandwidth panel drawing itself ────────── */

export function DashboardPreviewVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full items-center justify-center" aria-hidden="true">
      <div className="border-border bg-popover w-48 rounded-xl border p-3 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-[8px] font-semibold tracking-wider uppercase">
            Bandwidth
          </span>
          <span
            className="flex items-center gap-1 text-[8px]"
            style={{ color: "var(--brand-green)" }}
          >
            <span
              className="h-1 w-1 rounded-full"
              style={{ backgroundColor: "var(--brand-green)" }}
            />
            Live
          </span>
        </div>
        <svg viewBox="0 0 160 36" className="mt-1.5 h-9 w-full">
          <motion.path
            d="M0 30 C 18 28, 26 18, 44 20 S 74 8, 94 13 S 132 4, 160 8"
            fill="none"
            strokeWidth="2"
            strokeLinecap="round"
            style={{ stroke: "var(--brand-green)" }}
            initial={false}
            animate={reduced ? { pathLength: 1 } : { pathLength: [0, 0, 1, 1, 0] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.08, 0.44, 0.88, 0.94] }}
          />
        </svg>
        <div className="mt-1.5 flex items-center gap-2">
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.44, 0.5, 0.88, 0.94] }}
            className="text-foreground text-[10px] font-bold tabular-nums"
          >
            142.6 GB
          </motion.span>
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.5, 0.56, 0.88, 0.94] }}
            className="rounded-full px-1.5 py-px text-[8px] font-semibold"
            style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
          >
            99.6% success
          </motion.span>
        </div>
      </div>
    </div>
  );
}

/* ── Transparent Pricing — one number, nothing hidden ───────────────────── */

const PRICING_CHECKS = [
  { id: "p1", label: "No contracts", at: 0.3 },
  { id: "p2", label: "No hidden fees", at: 0.42 },
];

export function TransparentPricingVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-2" aria-hidden="true">
      <motion.div
        initial={false}
        animate={
          reduced
            ? { opacity: 1, scale: 1 }
            : { opacity: [0.4, 0.4, 1, 1, 0.4], scale: [0.9, 0.9, 1, 1, 0.9] }
        }
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.08, 0.14, 0.88, 0.94] }}
        className="border-border bg-popover flex items-baseline gap-1 rounded-xl border px-4 py-2 shadow-sm"
      >
        <span className="text-foreground text-[18px] font-bold tracking-tight tabular-nums">
          $2
        </span>
        <span className="text-muted-foreground text-[10px]">/GB · per month</span>
      </motion.div>
      <div className="flex items-center gap-2">
        {PRICING_CHECKS.map((check) => (
          <motion.span
            key={check.id}
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
            transition={
              reduced
                ? { duration: 0 }
                : { ...LOOP, times: [0, check.at, check.at + 0.06, 0.88, 0.94] }
            }
            className="text-muted-foreground flex items-center gap-1 text-[9px]"
          >
            <Check size={9} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
            {check.label}
          </motion.span>
        ))}
      </div>
    </div>
  );
}

/* ── Redundant Routing — live failover between routes ───────────────────── */

export function RedundantRoutingVisual() {
  const reduced = useReducedMotion();
  return (
    <div
      className="flex h-28 w-full flex-col items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      <div className="border-border bg-popover flex w-44 items-center justify-between rounded-lg border px-2.5 py-1.5 shadow-sm">
        <span className="text-foreground font-mono text-[9px]">route-a.proxi.es</span>
        <span className="relative flex h-3 items-center">
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [1, 1, 0, 0, 1] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.4, 0.46, 0.9, 0.96] }}
            className="absolute right-0 rounded-full px-1.5 py-px text-[8px] font-semibold whitespace-nowrap"
            style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
          >
            active
          </motion.span>
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 0 } : { opacity: [0, 0, 1, 1, 0] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.4, 0.46, 0.9, 0.96] }}
            className="absolute right-0 rounded-full px-1.5 py-px text-[8px] font-semibold whitespace-nowrap"
            style={{
              backgroundColor: "color-mix(in srgb, var(--amber-accent) 15%, transparent)",
              color: "var(--amber-accent)",
            }}
          >
            degraded
          </motion.span>
        </span>
      </div>
      <div className="border-border bg-popover flex w-44 items-center justify-between rounded-lg border px-2.5 py-1.5 shadow-sm">
        <span className="text-foreground font-mono text-[9px]">route-b.proxi.es</span>
        <span className="relative flex h-3 items-center">
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [1, 1, 0, 0, 1] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.4, 0.46, 0.9, 0.96] }}
            className="text-muted-foreground absolute right-0 text-[8px] whitespace-nowrap"
          >
            standby
          </motion.span>
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 0 } : { opacity: [0, 0, 1, 1, 0] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.4, 0.46, 0.9, 0.96] }}
            className="absolute right-0 rounded-full px-1.5 py-px text-[8px] font-semibold whitespace-nowrap"
            style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
          >
            active
          </motion.span>
        </span>
      </div>
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 0 } : { opacity: [0, 0, 1, 1, 0] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.48, 0.54, 0.88, 0.94] }}
        className="flex items-center gap-1 text-[8px] font-semibold tracking-wider uppercase"
        style={{ color: "var(--brand-green)" }}
      >
        <Check size={9} aria-hidden="true" /> Failover in 0.3s · zero drops
      </motion.span>
    </div>
  );
}

/* ── Datacenter Speeds — throughput ramping to 1 Gbps ───────────────────── */

export function DatacenterSpeedVisual() {
  const reduced = useReducedMotion();
  return (
    <div
      className="flex h-28 w-full flex-col items-center justify-center gap-2.5"
      aria-hidden="true"
    >
      <div className="border-border bg-popover w-48 rounded-xl border p-3 shadow-sm">
        <div className="flex items-center justify-between">
          <span className="text-muted-foreground text-[8px] font-semibold tracking-wider uppercase">
            Throughput
          </span>
          <motion.span
            initial={false}
            animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.36, 0.42, 0.88, 0.94] }}
            className="text-foreground text-[10px] font-bold tabular-nums"
          >
            1 Gbps
          </motion.span>
        </div>
        <div className="bg-foreground/10 mt-2 h-1.5 w-full overflow-hidden rounded-full">
          <motion.span
            initial={false}
            animate={reduced ? { scaleX: 1 } : { scaleX: [0, 0, 0.94, 0.94, 0] }}
            transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.08, 0.4, 0.9, 0.96] }}
            className="block h-full origin-left rounded-full"
            style={{
              background: "linear-gradient(90deg, var(--brand-green), var(--brand-yellow))",
            }}
          />
        </div>
        <div className="text-muted-foreground mt-1.5 flex justify-between font-mono text-[8px]">
          <span>0</span>
          <span>250M</span>
          <span>500M</span>
          <span>750M</span>
          <span>1G</span>
        </div>
      </div>
    </div>
  );
}

/* ── All Protocols — HTTP, HTTPS & SOCKS5 checking in ───────────────────── */

const PROTOCOLS = [
  { id: "pr1", label: "HTTP", at: 0.1 },
  { id: "pr2", label: "HTTPS", at: 0.2 },
  { id: "pr3", label: "SOCKS5", at: 0.3 },
];

export function ProtocolsVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-2" aria-hidden="true">
      <div className="flex items-center gap-1.5">
        {PROTOCOLS.map((proto) => (
          <motion.span
            key={proto.id}
            initial={false}
            animate={
              reduced
                ? { opacity: 1, y: 0 }
                : { opacity: [0.4, 0.4, 1, 1, 0.4], y: [5, 5, 0, 0, 5] }
            }
            transition={
              reduced
                ? { duration: 0 }
                : { ...LOOP, times: [0, proto.at, proto.at + 0.06, 0.88, 0.94] }
            }
            className="border-border bg-popover flex items-center gap-1 rounded-lg border px-2.5 py-1.5 shadow-sm"
          >
            <Check size={9} style={{ color: "var(--brand-green)" }} aria-hidden="true" />
            <span className="text-foreground font-mono text-[9px] font-semibold">
              {proto.label}
            </span>
          </motion.span>
        ))}
      </div>
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.42, 0.48, 0.88, 0.94] }}
        className="text-muted-foreground text-[9px]"
      >
        One endpoint, whatever your stack speaks
      </motion.span>
    </div>
  );
}

/* ── REST API & SDK — a request goes out, 201 comes back ────────────────── */

export function ApiSdkVisual() {
  const reduced = useReducedMotion();
  return (
    <div
      className="flex h-28 w-full flex-col items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      <motion.div
        initial={false}
        animate={
          reduced ? { opacity: 1, y: 0 } : { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] }
        }
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.08, 0.14, 0.88, 0.94] }}
        className="border-border bg-popover flex items-center gap-2 rounded-lg border px-3 py-1.5 shadow-sm"
      >
        <span
          className="rounded px-1.5 py-px font-mono text-[8px] font-bold"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          POST
        </span>
        <span className="text-foreground font-mono text-[9px]">/v1/access-points</span>
      </motion.div>
      <motion.div
        initial={false}
        animate={
          reduced ? { opacity: 1, y: 0 } : { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] }
        }
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.3, 0.36, 0.88, 0.94] }}
        className="border-border bg-popover flex items-center gap-2 rounded-lg border px-3 py-1.5 shadow-sm"
      >
        <span className="font-mono text-[9px] font-bold" style={{ color: "var(--brand-green)" }}>
          201
        </span>
        <span className="text-muted-foreground font-mono text-[9px]">
          {'{ "host": "pg.proxi.es" }'}
        </span>
      </motion.div>
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.48, 0.54, 0.88, 0.94] }}
        className="text-muted-foreground font-mono text-[8px]"
      >
        npm i @proxidize/sdk
      </motion.span>
    </div>
  );
}

/* ── 5-minute Setup — three steps checked off ───────────────────────────── */

const SETUP_STEPS = [
  { id: "s1", label: "Create an access point", at: 0.1 },
  { id: "s2", label: "Copy your credentials", at: 0.24 },
  { id: "s3", label: "Send your first request", at: 0.38 },
];

export function SetupStepsVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-2" aria-hidden="true">
      <div className="flex flex-col gap-1">
        {SETUP_STEPS.map((step) => (
          <span key={step.id} className="flex items-center gap-2">
            <span className="relative flex h-3.5 w-3.5 items-center justify-center">
              <span className="border-border bg-popover absolute inset-0 rounded-full border" />
              <motion.span
                initial={false}
                animate={
                  reduced
                    ? { opacity: 1, scale: 1 }
                    : { opacity: [0, 0, 1, 1, 0], scale: [0.6, 0.6, 1, 1, 0.6] }
                }
                transition={
                  reduced
                    ? { duration: 0 }
                    : { ...LOOP, times: [0, step.at, step.at + 0.06, 0.88, 0.94] }
                }
                className="absolute inset-0 flex items-center justify-center rounded-full"
                style={{ backgroundColor: "var(--brand-green)" }}
              >
                <Check size={8} style={{ color: "var(--background)" }} aria-hidden="true" />
              </motion.span>
            </span>
            <span className="text-foreground text-[10px]">{step.label}</span>
          </span>
        ))}
      </div>
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.5, 0.56, 0.88, 0.94] }}
        className="flex items-center gap-1 text-[8px] font-semibold tracking-wider uppercase"
        style={{ color: "var(--brand-green)" }}
      >
        <Check size={9} aria-hidden="true" /> Live in under 5 minutes
      </motion.span>
    </div>
  );
}

/* ── Dedicated Account Manager — a named human, on your account ─────────── */

export function AccountManagerVisual() {
  const reduced = useReducedMotion();
  return (
    <div className="flex h-28 w-full flex-col items-center justify-center gap-2" aria-hidden="true">
      <div className="border-border bg-popover flex items-center gap-2.5 rounded-xl border px-3.5 py-2 shadow-sm">
        <span
          className="relative flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold"
          style={{ backgroundColor: GREEN_TINT, color: "var(--brand-green)" }}
        >
          DK
          <span
            className="border-popover absolute -right-px -bottom-px h-2 w-2 rounded-full border-2"
            style={{ backgroundColor: "var(--brand-green)" }}
          />
        </span>
        <span className="flex flex-col">
          <span className="text-foreground text-[10px] leading-tight font-semibold">
            Dana K. · Account Manager
          </span>
          <span className="text-muted-foreground text-[9px] leading-tight">
            Knows your setup, answers directly
          </span>
        </span>
      </div>
      <motion.span
        initial={false}
        animate={
          reduced ? { opacity: 1, y: 0 } : { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] }
        }
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.34, 0.4, 0.88, 0.94] }}
        className="self-center rounded-2xl rounded-bl-md px-3 py-1.5 text-[9px] shadow-sm"
        style={{ backgroundColor: "var(--brand-green)", color: "var(--background)" }}
      >
        Your new pool is provisioned and ready.
      </motion.span>
    </div>
  );
}

/* ── Around the Clock — engineers online in every timezone ──────────────── */

const TIMEZONES = [
  { id: "tz1", city: "San Francisco", time: "02:14", at: 0.08 },
  { id: "tz2", city: "Berlin", time: "11:14", at: 0.18 },
  { id: "tz3", city: "Tokyo", time: "18:14", at: 0.28 },
];

export function TeamClockVisual() {
  const reduced = useReducedMotion();
  return (
    <div
      className="flex h-28 w-full flex-col items-center justify-center gap-1.5"
      aria-hidden="true"
    >
      {TIMEZONES.map((zone) => (
        <motion.span
          key={zone.id}
          initial={false}
          animate={
            reduced ? { opacity: 1, y: 0 } : { opacity: [0.4, 0.4, 1, 1, 0.4], y: [4, 4, 0, 0, 4] }
          }
          transition={
            reduced ? { duration: 0 } : { ...LOOP, times: [0, zone.at, zone.at + 0.06, 0.88, 0.94] }
          }
          className="border-border bg-popover flex w-40 items-center justify-between rounded-full border px-2.5 py-1 shadow-sm"
        >
          <span className="flex items-center gap-1.5">
            <span
              className="h-1 w-1 rounded-full"
              style={{ backgroundColor: "var(--brand-green)" }}
            />
            <span className="text-foreground text-[9px] font-medium">{zone.city}</span>
          </span>
          <span className="text-muted-foreground font-mono text-[9px] tabular-nums">
            {zone.time}
          </span>
        </motion.span>
      ))}
      <motion.span
        initial={false}
        animate={reduced ? { opacity: 1 } : { opacity: [0.4, 0.4, 1, 1, 0.4] }}
        transition={reduced ? { duration: 0 } : { ...LOOP, times: [0, 0.42, 0.48, 0.88, 0.94] }}
        className="flex items-center gap-1 text-[8px] font-semibold tracking-wider uppercase"
        style={{ color: "var(--brand-green)" }}
      >
        <Check size={9} aria-hidden="true" /> Real engineers online now
      </motion.span>
    </div>
  );
}
